"use client";

import { ChevronDownIcon, MessageSquareIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { cn } from "@/lib/utils";
import { QuickActionButton } from "./quick-action-button";
import type { QuickAction, QuickActionHandler } from "./types";

export type QuickActionsBarProps = {
  /** Array of quick actions to display */
  actions: QuickAction[];
  /** Handler called when an action is clicked */
  onAction: QuickActionHandler;
  /** Maximum number of visible actions before collapsing */
  maxVisible?: number;
  /** Optional title to display above actions */
  title?: string;
  /** Optional icon for the title */
  titleIcon?: ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Size variant for action buttons */
  size?: "sm" | "md" | "lg";
  /** Whether to show in compact mode (no title) */
  compact?: boolean;
};

export function QuickActionsBar({
  actions,
  onAction,
  maxVisible = 4,
  title = "Continue the conversation",
  titleIcon,
  className,
  size = "md",
  compact = false,
}: QuickActionsBarProps) {
  const [expanded, setExpanded] = useState(false);

  // Filter out hidden actions
  const visibleActions = actions.filter((action) => !action.hidden);

  if (visibleActions.length === 0) {
    return null;
  }

  const displayedActions = expanded
    ? visibleActions
    : visibleActions.slice(0, maxVisible);
  const hasMore = visibleActions.length > maxVisible;
  const hiddenCount = visibleActions.length - maxVisible;

  return (
    <div className={cn("space-y-2", className)}>
      {!compact && (
        <div className="flex items-center gap-2 text-muted-foreground text-xs">
          {titleIcon ?? <MessageSquareIcon className="size-3" />}
          <span>{title}</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {displayedActions.map((action, index) => (
          <QuickActionButton
            icon={action.icon}
            key={`${action.label}-${index}`}
            label={action.label}
            onClick={() => onAction(action.prompt, action.autoSend)}
            size={size}
            variant="default"
          />
        ))}

        {hasMore && !expanded && (
          <button
            className="inline-flex items-center gap-1 px-2 py-1 text-muted-foreground text-xs transition-colors hover:text-foreground"
            onClick={() => setExpanded(true)}
            type="button"
          >
            <span>+{hiddenCount} more</span>
            <ChevronDownIcon className="size-3" />
          </button>
        )}

        {expanded && hasMore && (
          <button
            className="inline-flex items-center gap-1 px-2 py-1 text-muted-foreground text-xs transition-colors hover:text-foreground"
            onClick={() => setExpanded(false)}
            type="button"
          >
            <span>Show less</span>
            <ChevronDownIcon className="size-3 rotate-180" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Inline variant for showing actions within content (e.g., on hover)
 */
export type InlineActionsProps = {
  actions: QuickAction[];
  onAction: QuickActionHandler;
  className?: string;
};

export function InlineActions({
  actions,
  onAction,
  className,
}: InlineActionsProps) {
  const visibleActions = actions.filter((action) => !action.hidden);

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <div className={cn("flex items-center gap-1", className)}>
      {visibleActions.map((action, index) => (
        <QuickActionButton
          icon={action.icon}
          key={`${action.label}-${index}`}
          label={action.label}
          onClick={() => onAction(action.prompt, action.autoSend)}
          showArrow={false}
          size="sm"
          variant="subtle"
        />
      ))}
    </div>
  );
}
